import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Stack, Grid, Alert, Divider, CircularProgress, MenuItem, Checkbox, FormControlLabel, Chip, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachmentIcon from '@mui/icons-material/Attachment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import DocumentPicker from './DocumentPicker';

// 7.1: Type of Enquiry options
const ENQUIRY_TYPE_OPTIONS = [
    'Section 415 Enquiry',
    'Section 152 Enquiry',
    'Section 417 & 418 Enquiry',
];

const emptyEnquiryForm = () => ({
    attorneyName: '',
    attorneyFirm: '',
    attorneyContact: '',
    attorneyEmail: '',
    enquiryType: '', // 7.1
    chairpersonName: '', // 7.2
    chairpersonSurname: '', // 7.2
    chairpersonReportObtained: false, // 7.3
    chairpersonReportDocumentUrl: null, // 7.3
    enquiryDates: '',
    closedDate: '',
    venue: '', // 7.6
    enquiryTime: '', // 7.6
    postponedDate: '', // 7.4
    transcriptInfo: '',
    witnesses: [], // 7.5: [{ id, fullName, contactDetails, subpoenaDocumentUrl }]
});

export default function EnquiryTab({ formData, handleFieldChange, isEditingMode, user, matterId, setDocuments }) {
    const enquiries = formData?.enquiriesList || [];

    // State for the currently edited/new enquiry
    const [editingId, setEditingId] = useState(null);
    const [enquiryForm, setEnquiryForm] = useState(emptyEnquiryForm());

    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    // 🎯 7.3 / 7.5: shared document picker - target is 'chairpersonReport' or `witness-<id>`
    const [docPickerOpen, setDocPickerOpen] = useState(false);
    const [docPickerTarget, setDocPickerTarget] = useState(null);

    const saveToDB = async (list) => {
        if (!matterId) return;
        try {
            await axios.put(`/api/legal-matter/${matterId}`, { enquiriesList: list });
        } catch (e) {
            console.error("Auto-save failed", e);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setEnquiryForm(emptyEnquiryForm());
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEnquiryForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = () => {
        if (!enquiryForm.attorneyName && !enquiryForm.venue) return;

        let updatedList = [...enquiries];
        if (editingId) {
            updatedList = updatedList.map(item => item.id === editingId ? { ...enquiryForm, id: editingId } : item);
        } else {
            updatedList.push({ ...enquiryForm, id: -Math.floor(Math.random() * 1000000) });
        }

        handleFieldChange({ target: { name: 'enquiriesList', value: updatedList, type: 'array' } });
        saveToDB(updatedList);
        resetForm();
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setEnquiryForm({ ...emptyEnquiryForm(), ...item, witnesses: Array.isArray(item.witnesses) ? item.witnesses : [] });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this enquiry record?')) {
            const updatedList = enquiries.filter(item => item.id !== id);
            handleFieldChange({ target: { name: 'enquiriesList', value: updatedList, type: 'array' } });
            saveToDB(updatedList);
        }
    };

    // --- 7.5: Multiple Witnesses & Subpoenas ---
    const handleAddWitness = () => {
        setEnquiryForm(prev => ({
            ...prev,
            witnesses: [...(prev.witnesses || []), { id: -Math.floor(Math.random() * 1000000), fullName: '', contactDetails: '', subpoenaDocumentUrl: null }],
        }));
    };

    const handleWitnessChange = (witnessId, field, value) => {
        setEnquiryForm(prev => ({
            ...prev,
            witnesses: (prev.witnesses || []).map(w => w.id === witnessId ? { ...w, [field]: value } : w),
        }));
    };

    const handleRemoveWitness = (witnessId) => {
        setEnquiryForm(prev => ({
            ...prev,
            witnesses: (prev.witnesses || []).filter(w => w.id !== witnessId),
        }));
    };

    const openDocPicker = (target) => {
        setDocPickerTarget(target);
        setDocPickerOpen(true);
    };

    const getActiveDocPath = () => {
        if (docPickerTarget === 'chairpersonReport') return enquiryForm.chairpersonReportDocumentUrl;
        if (docPickerTarget && String(docPickerTarget).startsWith('witness-')) {
            const witnessId = Number(String(docPickerTarget).replace('witness-', ''));
            return (enquiryForm.witnesses || []).find(w => w.id === witnessId)?.subpoenaDocumentUrl || null;
        }
        return null;
    };

    const handleDirectUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        const uploadData = new FormData();
        uploadData.append('folder', 'Enquiries & Litigation');
        uploadData.append('isPublic', 'false');

        Array.from(files).forEach(file => {
            uploadData.append('documents', file, file.name);
        });

        try {
            const response = await axios.post(`/api/legal-matter/${matterId}/uploads`, uploadData, {
                headers: {
                    'x-user-email': user?.emailAddress || '',
                    'x-user-role': user?.role || ''
                }
            });

            // 🎯 Automatically refresh the Documents tab state so they show up instantly!
            if (response.data?.files && setDocuments) {
                setDocuments(prev => [
                    ...prev,
                    ...response.data.files.map(f => ({ ...f, folder: 'Enquiries & Litigation', isPublic: false }))
                ]);
            }

            alert('Files successfully uploaded to the "Enquiries & Litigation" folder in the Documents tab!');
        } catch (error) {
            console.error("Upload error:", error);
            alert('Failed to upload files. Please try again.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const isDisabled = !isEditingMode || user?.role === 'guest';

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Enquiries & Litigation
                </Typography>
            </Stack>

            {/* 💡 Helper Alert for Audio/Transcripts */}
            <Alert severity="info" icon={<FolderOpenIcon />} sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Need to upload audio files, bundles, or transcripts?</Typography>
                        <Typography variant="body2">
                            The system supports uploading large audio files (.mp3, .wav), documents, and .zip bundles.
                            Click the button to upload them directly into the <strong>"Enquiries & Litigation"</strong> folder.
                        </Typography>
                    </Box>
                    <Box>
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept=".pdf,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.pptx,.mp3,.wav,.m4a,.zip"
                            onChange={handleDirectUpload}
                        />
                        <Button
                            variant="contained"
                            color="info"
                            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            disabled={uploading || isDisabled}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {uploading ? 'Uploading...' : 'Upload Files'}
                        </Button>
                    </Box>
                </Stack>
            </Alert>

            {/* 📝 CAPTURE / EDIT FORM */}
            <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: '#fcfcfc', borderTop: `4px solid ${editingId ? '#ff9800' : '#1976d2'}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {editingId ? 'Edit Enquiry Details' : 'Add New Enquiry'}
                </Typography>
                <Grid container spacing={2}>
                    {/* 7.1: Type of Enquiry */}
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth size="small" label="Type of Enquiry" name="enquiryType" value={enquiryForm.enquiryType || ''} onChange={handleFormChange} disabled={isDisabled}>
                            <MenuItem value=""><em>None selected</em></MenuItem>
                            {ENQUIRY_TYPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        {/* 7.6: Time of Enquiry */}
                        <TextField fullWidth size="small" type="time" label="Time of Enquiry" name="enquiryTime" InputLabelProps={{ shrink: true }} value={enquiryForm.enquiryTime || ''} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" type="date" label="Date Closed (If applicable)" InputLabelProps={{ shrink: true }} name="closedDate" value={enquiryForm.closedDate} onChange={handleFormChange} disabled={isDisabled} inputProps={{ max: '9999-12-31' }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Attorney Handling Enquiry</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Attorney Name" name="attorneyName" value={enquiryForm.attorneyName} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Law Firm Name" name="attorneyFirm" value={enquiryForm.attorneyFirm} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Contact Number" name="attorneyContact" value={enquiryForm.attorneyContact} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Email Address" type="email" name="attorneyEmail" value={enquiryForm.attorneyEmail} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>

                    {/* 7.2 / 7.3: Chairperson */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Chairperson</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="Chairperson Name" name="chairpersonName" value={enquiryForm.chairpersonName || ''} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="Chairperson Surname" name="chairpersonSurname" value={enquiryForm.chairpersonSurname || ''} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={<Checkbox checked={!!enquiryForm.chairpersonReportObtained} onChange={handleFormChange} name="chairpersonReportObtained" disabled={isDisabled} />}
                            label="Chairperson's Report Obtained"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            startIcon={enquiryForm.chairpersonReportDocumentUrl ? <VisibilityIcon /> : <AttachmentIcon />}
                            color={enquiryForm.chairpersonReportDocumentUrl ? "success" : "primary"}
                            disabled={isDisabled}
                            onClick={() => openDocPicker('chairpersonReport')}
                        >
                            {enquiryForm.chairpersonReportDocumentUrl ? "View Chairperson's Report" : "Link Chairperson's Report"}
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Enquiry Details</Typography>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        {/* 7.6: Venue */}
                        <TextField fullWidth size="small" label="Venue / Place of the Enquiry" name="venue" value={enquiryForm.venue} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        {/* 7.4: Postponed Enquiry Date */}
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Postponed Enquiry Date"
                            InputLabelProps={{ shrink: true }}
                            name="postponedDate"
                            value={enquiryForm.postponedDate || ''}
                            onChange={handleFormChange}
                            disabled={isDisabled}
                            inputProps={{ max: '9999-12-31' }}
                            helperText="You'll be reminded 1 week before this date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth multiline minRows={2} size="small" label="Enquiry Dates" placeholder="e.g. 12 May 2024, 15 June 2024..." name="enquiryDates" value={enquiryForm.enquiryDates} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth multiline minRows={2} size="small" label="Transcript Information / Notes" placeholder="e.g. Awaiting transcript from stenographer, bundle pending..." name="transcriptInfo" value={enquiryForm.transcriptInfo} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>

                    {/* 7.5: Multiple Witnesses & Subpoenas */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" color="textSecondary">Witnesses & Subpoenas</Typography>
                            <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={handleAddWitness} disabled={isDisabled}>
                                Add Witness
                            </Button>
                        </Stack>
                    </Grid>
                    {(enquiryForm.witnesses || []).length === 0 && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">No witnesses added yet.</Typography>
                        </Grid>
                    )}
                    {(enquiryForm.witnesses || []).map((witness, idx) => (
                        <Grid item xs={12} key={witness.id}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary">Witness {idx + 1}</Typography>
                                    <IconButton size="small" color="error" onClick={() => handleRemoveWitness(witness.id)} disabled={isDisabled}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={5}>
                                        <TextField fullWidth size="small" label="Witness Full Name" value={witness.fullName || ''} onChange={(e) => handleWitnessChange(witness.id, 'fullName', e.target.value)} disabled={isDisabled} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth size="small" label="Contact Details" value={witness.contactDetails || ''} onChange={(e) => handleWitnessChange(witness.id, 'contactDetails', e.target.value)} disabled={isDisabled} />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            startIcon={witness.subpoenaDocumentUrl ? <VisibilityIcon /> : <AttachmentIcon />}
                                            color={witness.subpoenaDocumentUrl ? "success" : "primary"}
                                            disabled={isDisabled}
                                            onClick={() => openDocPicker(`witness-${witness.id}`)}
                                        >
                                            {witness.subpoenaDocumentUrl ? 'Subpoena Attached' : 'Link Subpoena'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                            {editingId && (
                                <Button variant="outlined" onClick={resetForm} disabled={isDisabled}>Cancel Edit</Button>
                            )}
                            <Button
                                variant="contained"
                                color={editingId ? "warning" : "primary"}
                                onClick={handleSave}
                                disabled={isDisabled || (!enquiryForm.attorneyName && !enquiryForm.venue)}
                                startIcon={!editingId && <AddCircleOutlineIcon />}
                            >
                                {editingId ? 'Update Enquiry' : 'Add Enquiry'}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* 📊 SUMMARY TABLE */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Enquiries Summary</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type & Attorney</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Chairperson</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Venue, Time & Dates</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Postponed To</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Witnesses</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Closed</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {enquiries.map((item) => (
                            <TableRow key={item.id} sx={{ backgroundColor: item.closedDate ? '#fafafa' : 'inherit', opacity: item.closedDate ? 0.8 : 1 }}>
                                <TableCell>
                                    {item.enquiryType && <Chip label={item.enquiryType} size="small" sx={{ mb: 0.5 }} />}
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.attorneyName || 'N/A'}</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">{item.attorneyFirm}</Typography>
                                    <Typography variant="caption" color="text.secondary">{item.attorneyContact} {item.attorneyEmail && `| ${item.attorneyEmail}`}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{[item.chairpersonName, item.chairpersonSurname].filter(Boolean).join(' ') || '-'}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: item.chairpersonReportObtained ? 'success.main' : 'error.main' }} display="block">
                                        Report: {item.chairpersonReportObtained ? 'Obtained' : 'Outstanding'}
                                    </Typography>
                                    {item.chairpersonReportDocumentUrl && (
                                        <Button size="small" variant="text" onClick={() => window.open(`/api/${item.chairpersonReportDocumentUrl.replace(/\\/g, '/').replace(/^\/+/, '')}`, '_blank')}>
                                            Open Report
                                        </Button>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{item.venue || 'N/A'}{item.enquiryTime ? ` @ ${item.enquiryTime}` : ''}</Typography>
                                    <Typography variant="caption" color="text.secondary">{item.enquiryDates}</Typography>
                                </TableCell>
                                <TableCell>{item.postponedDate || '-'}</TableCell>
                                <TableCell>
                                    {(item.witnesses || []).length === 0 ? '-' : (
                                        <Stack spacing={0.5}>
                                            {item.witnesses.map((w, i) => (
                                                <Tooltip key={w.id} title={w.contactDetails || ''}>
                                                    <Chip
                                                        size="small"
                                                        label={`${w.fullName || `Witness ${i + 1}`}${w.subpoenaDocumentUrl ? ' 📎' : ''}`}
                                                        onClick={w.subpoenaDocumentUrl ? () => window.open(`/api/${w.subpoenaDocumentUrl.replace(/\\/g, '/').replace(/^\/+/, '')}`, '_blank') : undefined}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </Stack>
                                    )}
                                </TableCell>
                                <TableCell>{item.closedDate ? <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>{item.closedDate}</Typography> : 'No'}</TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <IconButton size="small" color="primary" onClick={() => handleEdit(item)} disabled={isDisabled}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)} disabled={isDisabled}><DeleteIcon fontSize="small" /></IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}

                        {enquiries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                                    No enquiries recorded yet. Add one using the form above.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Document Picker Modal - 7.3 Chairperson's Report / 7.5 Subpoenas */}
            <DocumentPicker
                open={docPickerOpen}
                onClose={() => setDocPickerOpen(false)}
                matterId={matterId}
                user={user}
                onDocumentSelect={(filepath) => {
                    if (docPickerTarget === 'chairpersonReport') {
                        setEnquiryForm(prev => ({ ...prev, chairpersonReportDocumentUrl: filepath }));
                    } else if (docPickerTarget && String(docPickerTarget).startsWith('witness-')) {
                        const witnessId = Number(String(docPickerTarget).replace('witness-', ''));
                        setEnquiryForm(prev => ({
                            ...prev,
                            witnesses: (prev.witnesses || []).map(w => w.id === witnessId ? { ...w, subpoenaDocumentUrl: filepath } : w),
                        }));
                    }
                    setDocPickerOpen(false);
                }}
                folder="Enquiries & Litigation"
                activeDocPath={getActiveDocPath()}
            />
        </Box>
    );
}
